/**
 * WordPopoverPanel Component
 * 
 * The comprehensive word detail popover for the Reader.
 * Shows definitions, translations, notes, status controls, and admin features.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Loader2,
  Volume2,
  Globe,
  X,
  RefreshCw,
  Minus,
  Lock,
  Unlock,
  Sparkles,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Layers,
  Pencil,
  Trash2,
  Check,
  AlertTriangle,
  MessageSquare,
  Bookmark,
  GripHorizontal,
  Crosshair,
  BookOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { CollapsibleSection } from './CollapsibleSection';
import { WordEntryStatus } from '../../shared/reader/WordEntryStatus';
import { api } from '../../utils/api';

const WORD_CATEGORIES = {
  1:'Daily Life',2:'Home and Household',3:'Food and Cooking',4:'Clothing and Accessories',
  5:'Body and Anatomy',6:'Medicine and Healthcare',7:'Psychology and Emotions',
  8:'Family and Relationships',9:'People and Professions',10:'Communication',
  11:'Language and Linguistics',12:'Education and Learning',13:'Society and Culture',
  14:'History and Archaeology',15:'Religion and Spirituality',16:'Philosophy and Ethics',
  17:'Mathematics and Statistics',18:'Physics',19:'Chemistry',20:'Biology and Life Sciences',
  21:'Astronomy and Space',22:'Research and Scientific Methods',23:'Nature and Outdoors',
  24:'Arts and Visual Design',25:'Literature and Writing',26:'Film and Entertainment',
  27:'Crafts and Skilled Trades',28:'Materials and Textiles',29:'Measurements and Units',
  30:'Energy and Power Systems',31:'Energy and Electricity',32:'Computing and Technology',
  33:'Engineering',34:'Mechanics and Tools',35:'Construction and Architecture',
  36:'Cities and Infrastructure',37:'Transportation and Vehicles',38:'Manufacturing and Industry',
  39:'Agriculture and Farming',40:'Business and Management',41:'Economics and Finance',
  42:'Law and Legal Systems',43:'Government and Politics',44:'Military and Defense',
  45:'Public Safety and Security',46:'Geography and Cartography',47:'Earth Science and Geology',
  48:'Weather and Climate',49:'Environment and Ecology',50:'Animals and Zoology',
  51:'Plants and Botany',52:'Travel and Tourism',53:'Sports and Fitness',54:'Games and Recreation',
  55:'Music and Performing Arts',56:'Media and Journalism',57:'Shopping and Commerce',
  58:'Real Estate and Property',59:'Law Enforcement and Criminal Justice',60:'General and Miscellaneous',
};

export function WordPopoverPanel({
  style,
  wordData,
  wordLoading,
  token,
  language,
  noteText,
  setNoteText,
  translationText,
  setTranslationText,
  translationSaved,
  onPlayAudio,
  isTTSSupported = true,
  onSetStatus,
  onSaveTranslation,
  onAutoTranslate,
  onSaveNote,
  onOpenSaveLang,
  onClose,
  onRefreshSection,
  onDeleteDefinition,
  onTogglePreferred,
  onUnlockWord,
  onUploadImage,
  onRemoveImage,
  onSaveEdit,
  onSubmitSuggestion,
  onToggleFieldLock,
  actionLoading,
  locked,
  lockedFields = [],
  isMobile,
  isAdmin,
  // Expression support
  selectedSpan,
  isExpressionMode,
  onExpandLeft,
  onExpandRight,
  onShrinkSelection,
  onSaveExpression,
  onEnrichExpression,
  expressionSaving,
  expressionTranslation,
  isExpressionInFullOracle,
  onOpenExpressionPanel,
  // Verb Formation support
  onSaveVerbFormation,
  verbFormationSaving,
  isVerbFormationInFullOracle,
  onOpenVerbFormationPanel,
  onEnrichVerbFormation,
  // Edit / Delete
  onDeleteExpression,
  onUpdateExpression,
  onDeleteVerbFormation,
  onUpdateVerbFormation,
  onOpenRelatedFormation,
  onAddFlashcard,
  onWordLookup,
  onDragHandleMouseDown,
  sentenceText = '',
  userTier = 1,
  onMastered,
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedDefinition, setEditedDefinition] = useState('');
  const [suggestionReason, setSuggestionReason] = useState('');
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [inContextLoading, setInContextLoading] = useState(false);
  const [inContextResult, setInContextResult] = useState(null);
  const [bottomExpanded, setBottomExpanded] = useState(false); // kept for content visibility
  const [panelHeight, setPanelHeight] = useState(null);        // null = default/auto
  const [noteEntries, setNoteEntries] = useState([]);
  const [manualNoteText, setManualNoteText] = useState('');
  const [exprEditMode, setExprEditMode] = useState(false);
  const [exprEditFields, setExprEditFields] = useState({});
  const [vfEditMode, setVfEditMode] = useState(false);
  const [vfEditFields, setVfEditFields] = useState({});
  const panelRef = useRef(null);
  const dragState = useRef(null); // { startY, startHeight }
  const fileInputRef = useRef(null);
  const currentStatus = wordData?.user_state?.status || 'new';

  const STATUS_COLORS = {
    new:      '#EF4444',
    learning: '#EAB308',
    familiar: '#22C55E',
    known:    'var(--foreground)',
    mastered: '#60A5FA',
    ignored:  'var(--muted-foreground)',
  };
  const wordTitleColor = STATUS_COLORS[currentStatus] ?? 'var(--foreground)';

  // Mastered milestone celebration
  const [showMasteredCelebration, setShowMasteredCelebration] = useState(false);
  const prevStatusRef = useRef(null);
  // Reset tracking when a new word opens so each mastered word triggers the celebration
  useEffect(() => { prevStatusRef.current = null; }, [token?.token_id]);
  useEffect(() => { setInContextResult(null); setBottomExpanded(false); setPanelHeight(null); setManualNoteText(''); }, [token?.token_id]);
  useEffect(() => {
    if (noteText) {
      try {
        const parsed = JSON.parse(noteText);
        if (Array.isArray(parsed)) { setNoteEntries(parsed); return; }
      } catch {}
      setNoteEntries([{ id: 'legacy_0', text: noteText, created_at: '' }]);
    } else {
      setNoteEntries([]);
    }
  }, [noteText]);

  // Bottom-tab drag-to-resize handlers
  const onResizePointerDown = (e) => {
    e.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;
    dragState.current = {
      startY: e.clientY ?? e.touches?.[0]?.clientY,
      startHeight: panel.getBoundingClientRect().height,
    };
    setBottomExpanded(true); // keep extended content visible while resizing

    const onMove = (ev) => {
      if (!dragState.current) return;
      const y = ev.clientY ?? ev.touches?.[0]?.clientY;
      const delta = y - dragState.current.startY;
      const newH = Math.min(
        Math.max(dragState.current.startHeight + delta, 180), // min 180px
        window.innerHeight * 0.92                              // max 92vh
      );
      setPanelHeight(newH);
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };
  // Fire when status reaches mastered — show oracle badge and notify Reader for confetti
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = currentStatus;
    if (currentStatus === 'mastered' && prev !== 'mastered') {
      setShowMasteredCelebration(true);
      const t = setTimeout(() => setShowMasteredCelebration(false), 2200);
      onMastered?.(token);
      return () => clearTimeout(t);
    }
  }, [currentStatus]);

  // Keyboard shortcuts: X / R / F / K / Escape — mirrors the shared WordPopover handler
  useEffect(() => {
    if (!token) return;
    const handler = (e) => {
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t.isContentEditable) return;
      const key = (e.key || '').toLowerCase();
      if (isExpressionMode && ['x', 'r', 'f', 'k'].includes(key)) return;
      let next = null;
      switch (key) {
        case 'x': next = 'ignored'; break;
        case 'r': next = 'learning'; break;
        case 'f': next = 'familiar'; break;
        case 'k': next = 'known'; break;
        case 'escape':
          if (onClose) { e.preventDefault(); onClose(); }
          return;
        default: return;
      }
      if (next && onSetStatus && !actionLoading) {
        e.preventDefault();
        onSetStatus(next);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [token, onSetStatus, onClose, actionLoading, isExpressionMode]);

  // Use surface form based on mode
  const displayWord = isVerbFormationInFullOracle && wordData?.word
    ? wordData.word
    : isExpressionInFullOracle && wordData?.word
      ? wordData.word
      : isExpressionMode && selectedSpan?.surfaceForm
        ? selectedSpan.surfaceForm
        : (token?.surface_form?.trim() || '');
  const word = displayWord;
  const langCode = language?.code || 'en';

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      await onUploadImage(file);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const searchLinks = [
    { label: 'Google Transl.', url: `https://translate.google.com/?sl=${langCode}&tl=en&text=${encodeURIComponent(word)}` },
    { label: 'Bing Transl.', url: `https://www.bing.com/translator?from=${langCode}&to=en&text=${encodeURIComponent(word)}` },
    { label: 'Google Search', url: `https://www.google.com/search?q=${encodeURIComponent(word)}` },
    { label: 'Reverso', url: `https://context.reverso.net/translation/${language?.name?.toLowerCase() || 'german'}-english/${encodeURIComponent(word)}` },
  ];

  // Auto-play audio when Oracle opens — sticky preference
  const [autoPlayAudio, setAutoPlayAudio] = useState(() => {
    try { return localStorage.getItem('iling_popover_auto_play_audio') !== '0'; } catch { return true; }
  });
  const autoPlayedRef = useRef(null);
  useEffect(() => {
    const key = `${displayWord}|${langCode}`;
    if (!token || !displayWord || !onPlayAudio || !autoPlayAudio || isExpressionMode) return;
    if (autoPlayedRef.current === key) return;
    autoPlayedRef.current = key;
    // 180 ms defer so popover is fully painted before audio starts
    const t = setTimeout(() => { try { onPlayAudio(); } catch (_) {} }, 180);
    // NOTE: intentionally no cleanup timer cancel — token ref can change during
    // enrichment merges, which would cancel before 180 ms and swallow auto-play.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, displayWord, langCode, autoPlayAudio]);

  const toggleAutoPlay = () => {
    setAutoPlayAudio(v => {
      const next = !v;
      try { localStorage.setItem('iling_popover_auto_play_audio', next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };

  const CONFETTI = [
    { color: '#FFD700', cx: '0px',   cy: '-52px', delay: '0ms'  },
    { color: '#22C55E', cx: '37px',  cy: '-37px', delay: '40ms' },
    { color: '#60A5FA', cx: '52px',  cy: '0px',   delay: '20ms' },
    { color: '#F472B6', cx: '37px',  cy: '37px',  delay: '60ms' },
    { color: '#A78BFA', cx: '0px',   cy: '52px',  delay: '10ms' },
    { color: '#FCD34D', cx: '-37px', cy: '37px',  delay: '50ms' },
    { color: '#34D399', cx: '-52px', cy: '0px',   delay: '30ms' },
    { color: '#FB923C', cx: '-37px', cy: '-37px', delay: '70ms' },
  ];

  const handleSaveContextNote = () => {
    if (!inContextResult) return;
    const entry = {
      id: Date.now().toString(),
      type: 'context',
      sentence: sentenceText,
      context_definition: inContextResult.context_definition,
      grammatical_role: inContextResult.grammatical_role,
      inflection_notes: inContextResult.inflection_notes,
      literal_vs_contextual: inContextResult.literal_vs_contextual,
      created_at: new Date().toISOString(),
    };
    const updated = [...noteEntries, entry];
    setNoteEntries(updated);
    onSaveNote(JSON.stringify(updated));
  };

  const handleAddManualNote = () => {
    if (!manualNoteText.trim()) return;
    const entry = { id: Date.now().toString(), type: 'manual', text: manualNoteText.trim(), created_at: new Date().toISOString() };
    const updated = [...noteEntries, entry];
    setNoteEntries(updated);
    setManualNoteText('');
    onSaveNote(JSON.stringify(updated));
  };

  const handleDeleteNote = (id) => {
    const updated = noteEntries.filter(e => e.id !== id);
    setNoteEntries(updated);
    onSaveNote(JSON.stringify(updated));
  };

  return (
    <div
      data-popover-panel="true"
      data-testid="word-popover"
      className="forge-popover z-[9999]"
      ref={panelRef}
      style={{ 
        ...style, 
        position: 'fixed',
        width: isMobile ? 'calc(100vw - 20px)' : 380,
        maxWidth: isMobile ? 'calc(100vw - 20px)' : 380,
        maxHeight: panelHeight ? `${panelHeight}px` : (style?.maxHeight || '85vh'),
        height: panelHeight ? `${panelHeight}px` : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Drag handle */}
      <div
        data-drag-handle
        onMouseDown={onDragHandleMouseDown}
        onTouchStart={onDragHandleMouseDown}
        className="flex items-center justify-center w-full select-none"
        style={{
          height: 20,
          cursor: 'grab',
          flexShrink: 0,
          borderRadius: '10px 10px 0 0',
          background: 'transparent',
        }}
        title="Drag to reposition"
      >
        <GripHorizontal className="h-3.5 w-3.5 opacity-30 hover:opacity-60 transition-opacity" />
      </div>
      {wordLoading ? (
        <div className="flex items-center justify-center p-8" data-testid="word-popover-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (wordData || isExpressionMode) ? (
        <>
          <WordEntryStatus data={wordData} />
          {/* Word Image - only show if wordData exists */}
          {wordData?.image_url && (
            <div className="relative flex-shrink-0">
              <img 
                src={wordData.image_url} 
                alt={word}
                className="w-full aspect-square object-cover"
                style={{ 
                  borderTopLeftRadius: 16, 
                  borderTopRightRadius: 16,
                  display: 'block'
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {isAdmin && (
                <button
                  onClick={onRemoveImage}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full shadow-lg"
                  title="Remove image"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ minHeight: 0 }}>
            {/* Header */}
            <div className="forge-header">
              {/* Expression Mode Badge */}
              {isExpressionMode && !isVerbFormationInFullOracle && (
                <div className="flex items-center justify-center gap-1 mb-2" data-testid="expression-mode-badge">
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                    <Layers size={12} />
                    Expression Mode ({selectedSpan?.tokens?.length || 0} words)
                  </span>
                </div>
              )}
              {/* Verb Formation Full Oracle badge */}
              {isVerbFormationInFullOracle && (
                <div className="flex items-center justify-center gap-1 mb-2" data-testid="vf-full-oracle-badge">
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                    <BookOpen size={12} />
                    Verb Formation
                  </span>
                </div>
              )}
              {/* Expression Full Oracle badge */}
              {isExpressionInFullOracle && (
                <div className="flex items-center justify-center gap-1 mb-2" data-testid="expression-full-oracle-badge">
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                    <Layers size={12} />
                    Expression
                  </span>
                </div>
              )}
              
              {/* Word/Expression Row with Expansion Controls */}
              <div className="forge-word-row" style={{ alignItems: 'center' }}>
                {/* Left Expand Button */}
                {onExpandLeft && (
                  <button 
                    className="forge-expand-btn" 
                    title="Expand selection left" 
                    onClick={onExpandLeft}
                    data-testid="expand-left-btn"
                  >
                    <ChevronLeft size={18} />
                  </button>
                )}
                
                <h1 
                  className={`forge-word-title ${isExpressionMode ? 'expression-title' : ''} ${isVerbFormationInFullOracle ? 'verb-formation-title' : ''}`}
                  data-testid="popover-word"
                  style={isVerbFormationInFullOracle
                    ? { color: '#22C55E', fontSize: '1.1rem', lineHeight: '1.3' }
                    : isExpressionMode
                      ? { fontSize: '1.1rem', lineHeight: '1.3' }
                      : { color: wordTitleColor, transition: 'color 0.2s' }
                  }
                >
                  {word}
                </h1>
                
                {/* Right Expand Button */}
                {onExpandRight && (
                  <button 
                    className="forge-expand-btn" 
                    title="Expand selection right" 
                    onClick={onExpandRight}
                    data-testid="expand-right-btn"
                  >
                    <ChevronRight size={18} />
                  </button>
                )}
                
                {isTTSSupported && !isExpressionMode && (
                  <button className="forge-speaker-btn" title="Play audio" onClick={onPlayAudio} data-testid="play-audio-btn">
                    <Volume2 size={22} />
                  </button>
                )}
                {isTTSSupported && !isExpressionMode && (
                  <button
                    className={`ml-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${autoPlayAudio ? 'bg-primary/20 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    title={autoPlayAudio ? 'Auto-play on open — click to disable' : 'Auto-play on open — click to enable'}
                    onClick={toggleAutoPlay}
                    data-testid="auto-play-toggle"
                  >
                    AUTO
                  </button>
                )}
              </div>
              
              {/* Shrink button when in expression mode */}
              {isExpressionMode && onShrinkSelection && (
                <div className="flex justify-center mt-1">
                  <button 
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-1 rounded hover:bg-muted/50 transition-colors"
                    onClick={onShrinkSelection}
                    title="Shrink selection"
                    data-testid="shrink-selection-btn"
                  >
                    <Minus size={12} /> Shrink
                  </button>
                </div>
              )}

              {/* "Open in Expression Panel" when expression is in full Oracle mode */}
              {isExpressionInFullOracle && onOpenExpressionPanel && (
                <div className="flex justify-center mt-1" style={{ paddingRight: '70px' }}>
                  <button
                    onClick={onOpenExpressionPanel}
                    className="text-xs text-purple-400 hover:text-purple-300 border border-purple-500/40 hover:border-purple-400/60 rounded px-2 py-0.5 transition-colors flex items-center gap-1"
                    data-testid="open-in-expression-panel-btn"
                  >
                    <Layers size={12} /> Open in Expression Panel
                  </button>
                </div>
              )}

              {/* Expression Full Oracle — Edit / Delete toolbar */}
              {isExpressionInFullOracle && (onUpdateExpression || onDeleteExpression) && (
                <div className="flex justify-center gap-2 mt-2 px-2">
                  {onUpdateExpression && (
                    <button
                      className="text-xs text-purple-400 hover:text-purple-300 border border-purple-500/30 hover:border-purple-400/50 rounded px-2 py-0.5 transition-colors flex items-center gap-1"
                      onClick={() => {
                        setExprEditMode(v => !v);
                        setExprEditFields({ translation_en: wordData?.translation_en || '', notes: wordData?.notes || '' });
                      }}
                      data-testid="expression-edit-btn"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                  {onDeleteExpression && (
                    <button
                      className="text-xs text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-400/50 rounded px-2 py-0.5 transition-colors flex items-center gap-1"
                      onClick={onDeleteExpression}
                      data-testid="expression-delete-btn"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                </div>
              )}

              {/* Expression inline edit form */}
              {isExpressionInFullOracle && exprEditMode && (
                <div className="mx-3 mt-2 p-2.5 rounded-lg border border-purple-500/30 bg-purple-500/8 space-y-2">
                  <div>
                    <label className="text-xs text-purple-400/70 uppercase tracking-wider">Translation</label>
                    <input
                      className="w-full mt-1 bg-transparent border border-purple-500/20 rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-purple-400/60"
                      value={exprEditFields.translation_en || ''}
                      onChange={e => setExprEditFields(f => ({ ...f, translation_en: e.target.value }))}
                      placeholder="English translation…"
                      data-testid="expression-edit-translation"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-purple-400/70 uppercase tracking-wider">Notes</label>
                    <textarea
                      className="w-full mt-1 bg-transparent border border-purple-500/20 rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-purple-400/60 resize-none"
                      rows={2}
                      value={exprEditFields.notes || ''}
                      onChange={e => setExprEditFields(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Notes…"
                      data-testid="expression-edit-notes"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded" onClick={() => setExprEditMode(false)}>Cancel</button>
                    <button
                      className="text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-3 py-1 rounded transition-colors"
                      onClick={async () => { await onUpdateExpression?.(exprEditFields); setExprEditMode(false); }}
                      data-testid="expression-edit-save-btn"
                    >Save</button>
                  </div>
                </div>
              )}
              
              {/* Expression Actions - Save & AI Enrich */}
              {isExpressionMode && selectedSpan?.tokens?.length > 1 && (
                <div className="flex justify-center gap-2 mt-2 px-2 flex-wrap">
                  {onSaveExpression && (
                    <button 
                      className="flex-1 min-w-[120px] text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                      onClick={onSaveExpression}
                      disabled={expressionSaving || verbFormationSaving}
                      title="Save as expression"
                      data-testid="save-expression-popover-btn"
                    >
                      {expressionSaving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      Save Expression
                    </button>
                  )}
                  {onSaveVerbFormation && (
                    <button
                      className="flex-1 min-w-[120px] text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                      onClick={onSaveVerbFormation}
                      disabled={expressionSaving || verbFormationSaving}
                      title="Save as verb formation"
                      data-testid="save-verb-formation-popover-btn"
                    >
                      {verbFormationSaving ? <Loader2 size={12} className="animate-spin" /> : <BookOpen size={12} />}
                      Save Verb Formation
                    </button>
                  )}
                  {onEnrichExpression && (
                    <button 
                      className="flex-1 min-w-[120px] text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                      onClick={onEnrichExpression}
                      disabled={expressionSaving || verbFormationSaving}
                      title="Fill with AI"
                      data-testid="enrich-expression-popover-btn"
                    >
                      {expressionSaving ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      AI Fill
                    </button>
                  )}
                </div>
              )}

              {/* Full Oracle expression mode — AI Enrich when no definitions yet */}
              {isExpressionInFullOracle && onEnrichExpression && !wordData?.definitions?.length && (
                <div className="flex justify-center mt-2 px-2">
                  <button
                    className="w-full text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                    onClick={onEnrichExpression}
                    disabled={expressionSaving}
                    title="Enrich with AI"
                    data-testid="enrich-expression-full-oracle-btn"
                  >
                    {expressionSaving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    AI Enrich Expression
                  </button>
                </div>
              )}

              {/* Verb Formation Full Oracle — AI Enrich */}
              {isVerbFormationInFullOracle && onEnrichVerbFormation && (
                <div className="flex justify-center mt-2 px-2">
                  <button
                    className="w-full text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                    onClick={onEnrichVerbFormation}
                    title="Enrich with AI"
                    data-testid="enrich-vf-full-oracle-btn"
                  >
                    <Sparkles size={12} />
                    AI Enrich Verb Formation
                  </button>
                </div>
              )}

              {/* Verb Formation Full Oracle — Edit / Delete toolbar */}
              {isVerbFormationInFullOracle && (onUpdateVerbFormation || onDeleteVerbFormation) && (
                <div className="flex justify-center gap-2 mt-2 px-2">
                  {onUpdateVerbFormation && (
                    <button
                      className="text-xs text-green-400 hover:text-green-300 border border-green-500/30 hover:border-green-400/50 rounded px-2 py-0.5 transition-colors flex items-center gap-1"
                      onClick={() => {
                        setVfEditMode(v => !v);
                        setVfEditFields({
                          translation_en: wordData?.translation_en || '',
                          formation_type: wordData?.formation_type || '',
                          notes: wordData?.notes || '',
                        });
                      }}
                      data-testid="vf-edit-btn"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                  {onDeleteVerbFormation && (
                    <button
                      className="text-xs text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-400/50 rounded px-2 py-0.5 transition-colors flex items-center gap-1"
                      onClick={onDeleteVerbFormation}
                      data-testid="vf-delete-btn"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                </div>
              )}

              {/* VF inline edit form */}
              {isVerbFormationInFullOracle && vfEditMode && (
                <div className="mx-3 mt-2 p-2.5 rounded-lg border border-green-500/30 bg-green-500/8 space-y-2">
                  <div>
                    <label className="text-xs text-green-400/70 uppercase tracking-wider">Translation</label>
                    <input
                      className="w-full mt-1 bg-transparent border border-green-500/20 rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-green-400/60"
                      value={vfEditFields.translation_en || ''}
                      onChange={e => setVfEditFields(f => ({ ...f, translation_en: e.target.value }))}
                      placeholder="English translation…"
                      data-testid="vf-edit-translation"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-green-400/70 uppercase tracking-wider">Formation Type</label>
                    <input
                      className="w-full mt-1 bg-transparent border border-green-500/20 rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-green-400/60"
                      value={vfEditFields.formation_type || ''}
                      onChange={e => setVfEditFields(f => ({ ...f, formation_type: e.target.value }))}
                      placeholder="e.g. light-verb, compound-verb…"
                      data-testid="vf-edit-formation-type"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-green-400/70 uppercase tracking-wider">Notes</label>
                    <textarea
                      className="w-full mt-1 bg-transparent border border-green-500/20 rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-green-400/60 resize-none"
                      rows={2}
                      value={vfEditFields.notes || ''}
                      onChange={e => setVfEditFields(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Notes…"
                      data-testid="vf-edit-notes"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded" onClick={() => setVfEditMode(false)}>Cancel</button>
                    <button
                      className="text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 px-3 py-1 rounded transition-colors"
                      onClick={async () => { await onUpdateVerbFormation?.(vfEditFields); setVfEditMode(false); }}
                      data-testid="vf-edit-save-btn"
                    >Save</button>
                  </div>
                </div>
              )}
              
              {wordData?.ipa && !isExpressionMode && (
                <div className="forge-phonetics space-y-0.5">
                  <span>/{wordData.ipa}/</span>
                  {/* Chinese: Zhuyin + Jyutping */}
                  {wordData?.zhuyin && <span className="text-xs text-cyan-400 ml-2">{wordData.zhuyin}</span>}
                  {wordData?.jyutping && <span className="text-xs text-amber-400 ml-2">粵 {wordData.jyutping}</span>}
                  {/* Japanese: Romaji */}
                  {wordData?.romaji && <span className="text-xs text-pink-400 ml-2">{wordData.romaji}</span>}
                  {/* Dialect IPA variants */}
                  {Array.isArray(wordData?.ipa_variants) && wordData.ipa_variants.length > 0 && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      {wordData.ipa_variants.map((v, i) => (
                        <span key={i} className="text-xs text-muted-foreground">
                          {v.dialect}: <span className="text-foreground">/{v.ipa}/</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Mastered milestone badge — brief glow in oracle header */}
              {showMasteredCelebration && (
                <div className="mastered-milestone-badge">
                  ★ MASTERED ★
                </div>
              )}

              <div className="forge-top-icons">
                <button className="forge-close-btn forge-close-btn--close" title="Close" onClick={onClose} data-testid="close-popover-btn">&times;</button>
                <button
                  className="forge-close-btn forge-close-btn--lang"
                  title="Save word in another language"
                  onClick={onOpenSaveLang}
                  data-testid="save-lang-icon"
                  style={{ fontSize: 0 }}
                >
                  <Globe size={15} />
                </button>
                {onAddFlashcard && (
                  <button
                    className="forge-close-btn forge-close-btn--flash"
                    title="Add to flashcard list"
                    onClick={onAddFlashcard}
                    data-testid="add-flashcard-icon"
                    style={{ fontSize: 0 }}
                  >
                    <Bookmark size={15} />
                  </button>
                )}
              </div>
            </div>

            <div className="forge-divider" />

            {/* Admin: Browse Image - only show for single word mode with wordData */}
            {isAdmin && wordData && !wordData.image_url && !isExpressionMode && !isVerbFormationInFullOracle && (
              <>
                <div className="px-2 py-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-full py-2 px-3 text-sm border border-dashed rounded-lg text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors flex items-center justify-center gap-2"
                    data-testid="browse-image-btn"
                  >
                    {uploadingImage ? (
                      <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                    ) : (
                      <><ImageIcon size={16} /> Browse Image (Admin)</>
                    )}
                  </button>
                </div>
                <div className="forge-divider" />
              </>
            )}

            {/* In Context (Tier 3) - shown below browse image, always accessible */}
            {userTier >= 3 && sentenceText && wordData && !wordData.lookup_pending && !isExpressionMode && !isVerbFormationInFullOracle && (
              <>
                <div className="px-1 pb-1">
                  {!inContextResult && (
                    <button
                      data-testid="in-context-btn"
                      disabled={inContextLoading}
                      onClick={async () => {
                        setInContextLoading(true);
                        try {
                          const { data } = await api.post('/vocabulary/in-context', {
                            word: wordData.lemma || token?.normalized_form || '',
                            surface_form: token?.surface_form || '',
                            language_code: wordData.language_code || '',
                            language_name: wordData.language_name || '',
                            sentence: sentenceText,
                          });
                          setInContextResult(data);
                        } catch {
                          setInContextResult({ context_definition: 'Analysis failed. Please try again.', grammatical_role: null, inflection_notes: null, literal_vs_contextual: null });
                        } finally {
                          setInContextLoading(false);
                        }
                      }}
                      className="w-full flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs font-medium hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                    >
                      {inContextLoading
                        ? <><Loader2 className="h-3 w-3 animate-spin" /> Analyzing context…</>
                        : <><Crosshair className="h-3 w-3" /> In Context</>
                      }
                    </button>
                  )}
                  {inContextResult && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 p-2.5 text-xs space-y-1.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                          <Crosshair className="h-3 w-3" /> In Context
                        </span>
                        <button onClick={() => setInContextResult(null)} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
                      </div>
                      <p className="leading-relaxed">{inContextResult.context_definition}</p>
                      {inContextResult.grammatical_role && (
                        <div className="flex gap-1.5 items-start">
                          <span className="text-amber-400/70 shrink-0">Role:</span>
                          <span className="text-muted-foreground">{inContextResult.grammatical_role}</span>
                        </div>
                      )}
                      {inContextResult.inflection_notes && (
                        <div className="flex gap-1.5 items-start">
                          <span className="text-amber-400/70 shrink-0">Why this form:</span>
                          <span className="text-muted-foreground">{inContextResult.inflection_notes}</span>
                        </div>
                      )}
                      {inContextResult.literal_vs_contextual && (
                        <div className="flex gap-1.5 items-start">
                          <span className="text-amber-400/70 shrink-0">Note:</span>
                          <span className="text-muted-foreground italic">{inContextResult.literal_vs_contextual}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-1">
                        <button onClick={() => { setInContextResult(null); setInContextLoading(false); }}
                          className="text-[10px] text-amber-400/60 hover:text-amber-400 underline">re-analyze</button>
                        <button onClick={handleSaveContextNote}
                          className="text-[10px] text-amber-400/60 hover:text-amber-400 flex items-center gap-1 underline"
                          data-testid="save-context-to-notes-btn">
                          Save to Notes
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="forge-divider" />
              </>
            )}

            {/* Wrong Language - shown below In Context */}
            {!isExpressionMode && !isVerbFormationInFullOracle && wordData && (
              !showSuggestionForm ? (
                <div className="forge-center-row py-1">
                  <button 
                    className="text-xs text-muted-foreground hover:text-amber-500 flex items-center gap-1 transition-colors"
                    onClick={() => setShowSuggestionForm(true)}
                    data-testid="report-language-btn"
                  >
                    <AlertTriangle size={12} />
                    Wrong language?
                  </button>
                </div>
              ) : (
                <div className="p-2 bg-amber-500/10 rounded-lg space-y-2 mx-2">
                  <div className="flex items-center gap-2 text-xs text-amber-500">
                    <AlertTriangle size={14} />
                    <span className="font-medium">Report Language Issue</span>
                  </div>
                  <Input
                    value={suggestionReason}
                    onChange={(e) => setSuggestionReason(e.target.value)}
                    placeholder="What is the correct language? (e.g., 'This is Spanish, not Portuguese')"
                    className="text-sm"
                    data-testid="language-issue-input"
                  />
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => { setShowSuggestionForm(false); setSuggestionReason(''); }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      onClick={() => {
                        if (onSubmitSuggestion && suggestionReason.trim()) {
                          onSubmitSuggestion('language_issue', suggestionReason, `Language misidentification report for "${word}"`);
                        }
                        setShowSuggestionForm(false);
                        setSuggestionReason('');
                      }}
                      disabled={!suggestionReason.trim()}
                      data-testid="submit-language-issue-btn"
                    >
                      <MessageSquare size={14} className="mr-1" />
                      Report
                    </Button>
                  </div>
                </div>
              )
            )}

            {/* Quick Definitions (collapsed view) - only show for single word mode with wordData */}
            {!showDetails && !isExpressionMode && !isVerbFormationInFullOracle && wordData && (
              <>
                <div className="px-2 py-1">
                  {Array.isArray(wordData.definitions) && wordData.definitions.slice(0, 2).map((def, idx) => (
                    <div key={`quick-def-${idx}`} className="text-sm mb-1">
                      {def.pos && <span className="font-medium text-primary mr-1">[{def.pos}]</span>}
                      <span>{def.definition}</span>
                    </div>
                  ))}
                  {!Array.isArray(wordData.definitions) || !wordData.definitions.length ? <p className="text-sm text-muted-foreground">No definitions yet</p> : null}
                </div>
                <div className="forge-divider" />
              </>
            )}

            {/* Expression Mode - placeholder for future expression data */}
            {isExpressionMode && (
              <div className="px-4 py-3">
                <p className="text-sm text-muted-foreground text-center">
                  Expression selected: <span className="font-medium text-foreground">{selectedSpan?.tokens?.length || 0} words</span>
                </p>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Use ← → to adjust selection
                </p>
              </div>
            )}

            {/* ── Verb Formation Full Oracle Body ── */}
            {isVerbFormationInFullOracle && wordData && (
              <div className="px-3 py-2 space-y-3" data-testid="verb-formation-body">

                {/* IPA */}
                {wordData.ipa && (
                  <div className="text-xs text-muted-foreground">/{wordData.ipa}/</div>
                )}

                {/* Translation */}
                {(wordData.translation_en || wordData.translation) && (
                  <div className="rounded-lg bg-green-500/10 border border-green-500/20 px-3 py-2">
                    <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Meaning</p>
                    <p className="text-sm text-foreground">{wordData.translation_en || wordData.translation}</p>
                  </div>
                )}

                {/* Definitions */}
                {Array.isArray(wordData.definitions) && wordData.definitions.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-green-400 uppercase tracking-wider">Definition</p>
                    {wordData.definitions.map((def, i) => (
                      <div key={i} className="text-sm text-foreground leading-relaxed">
                        {def.pos && <span className="text-xs text-green-400/70 mr-1.5">[{def.pos}]</span>}
                        {def.definition}
                      </div>
                    ))}
                  </div>
                )}

                <div className="forge-divider" />

                {/* Components */}
                {Array.isArray(wordData.components) && wordData.components.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Components</p>
                    <div className="flex flex-wrap gap-1.5">
                      {wordData.components.map((comp, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 rounded-full text-sm border border-green-500/30 bg-green-500/10 text-green-300 cursor-pointer hover:bg-green-500/20 transition-colors"
                          title={`Look up "${comp}"`}
                          onClick={() => onWordLookup && onWordLookup(comp)}
                          data-testid={`vf-component-${i}`}
                        >
                          {comp}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lemma (if different from word) */}
                {wordData.lemma && wordData.lemma !== wordData.word && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px]">Lemma</span>
                    <span className="text-foreground font-medium">{wordData.lemma}</span>
                  </div>
                )}

                {/* Verb Element */}
                {wordData.verb_element && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px] pt-0.5">Verb</span>
                    <div>
                      <span className="text-green-300 font-medium">{wordData.verb_element}</span>
                      {wordData.verb_element_meaning && (
                        <span className="text-muted-foreground ml-2 text-xs">— {wordData.verb_element_meaning}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Non-verb element meaning */}
                {wordData.non_verb_element_meaning && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px] pt-0.5">Noun part</span>
                    <span className="text-muted-foreground text-xs">{wordData.non_verb_element_meaning}</span>
                  </div>
                )}

                {/* Formation type */}
                {wordData.formation_type && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px]">Type</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/15 border border-green-500/25 text-green-400">
                      {wordData.formation_type}
                    </span>
                  </div>
                )}

                {/* Morphology */}
                {wordData.morphology && (
                  <div>
                    <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Morphology</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{wordData.morphology}</p>
                  </div>
                )}

                {/* Context sentence */}
                {wordData.context_sentence && (
                  <>
                    <div className="forge-divider" />
                    <div>
                      <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Saved From</p>
                      <p className="text-xs text-muted-foreground italic leading-relaxed">{wordData.context_sentence}</p>
                    </div>
                  </>
                )}

                {/* Example sentences */}
                {Array.isArray(wordData.examples) && wordData.examples.length > 0 && (
                  <>
                    <div className="forge-divider" />
                    <div>
                      <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Examples</p>
                      <div className="space-y-1.5">
                        {wordData.examples.map((ex, i) => (
                          <p key={i} className="text-sm text-foreground leading-relaxed border-l-2 border-green-500/30 pl-2.5">{ex}</p>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Example sentences inside definitions */}
                {Array.isArray(wordData.definitions) && wordData.definitions.some(d => Array.isArray(d.examples) && d.examples.length) && (
                  <>
                    {!Array.isArray(wordData.examples) || wordData.examples.length === 0 ? (
                      <>
                        <div className="forge-divider" />
                        <div>
                          <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Examples</p>
                          <div className="space-y-1.5">
                            {wordData.definitions.flatMap(d => d.examples || []).map((ex, i) => (
                              <p key={i} className="text-sm text-foreground leading-relaxed border-l-2 border-green-500/30 pl-2.5">{ex}</p>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : null}
                  </>
                )}

                {/* Related formations */}
                {Array.isArray(wordData.related_formations) && wordData.related_formations.length > 0 && (
                  <>
                    <div className="forge-divider" />
                    <div>
                      <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Related Formations</p>
                      <div className="flex flex-wrap gap-1.5">
                        {wordData.related_formations.map((rf, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 rounded text-xs border border-green-500/20 bg-green-500/8 text-green-300/80 cursor-pointer hover:bg-green-500/20 hover:text-green-300 transition-colors flex items-center gap-1"
                            onClick={() => onOpenRelatedFormation ? onOpenRelatedFormation(rf) : onWordLookup && onWordLookup(rf)}
                            title={`Open "${rf}" formation`}
                            data-testid={`vf-related-${i}`}
                          >
                            <BookOpen size={10} />
                            {rf}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Notes */}
                {wordData.notes && (
                  <>
                    <div className="forge-divider" />
                    <div>
                      <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Notes</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{wordData.notes}</p>
                    </div>
                  </>
                )}

                {/* AI enrichment status */}
                {!wordData.ai_enriched && (
                  <p className="text-xs text-muted-foreground/50 text-center pt-1">AI enrichment pending…</p>
                )}

                <div className="forge-divider" />
              </div>
            )}

            {/* Expanded Details - only show for single word mode with wordData */}
            {(showDetails || bottomExpanded) && wordData && !isExpressionMode && !isVerbFormationInFullOracle && (
              <>
                {/* Hebrew Prefix Morphology Breakdown */}
                {token?.metadata?.hebrew_morph && (
                  <>
                    <CollapsibleSection title="Morphological Breakdown:" defaultOpen>
                      <div className="p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20 space-y-2">
                        <div className="flex items-center gap-2 text-base font-semibold" dir="rtl">
                          <span className="text-purple-400">{token.metadata.hebrew_morph.prefix}</span>
                          <span className="text-muted-foreground text-xs">+</span>
                          <span>{token.metadata.hebrew_morph.stem}</span>
                        </div>
                        <div className="flex flex-col gap-1 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-300 min-w-[50px]">Prefix:</span>
                            <span className="font-medium" dir="rtl">{token.metadata.hebrew_morph.prefix}</span>
                            <span className="text-muted-foreground">({token.metadata.hebrew_morph.prefix_meaning})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-purple-300 min-w-[50px]">Stem:</span>
                            <span className="font-medium" dir="rtl">{token.metadata.hebrew_morph.stem}</span>
                          </div>
                        </div>
                      </div>
                    </CollapsibleSection>
                    <div className="forge-divider" />
                  </>
                )}

                {/* Definitions with Edit/Lock */}
                <CollapsibleSection 
                  title={
                    <div className="flex items-center gap-2">
                      <span>Definitions{Array.isArray(wordData.definitions) && wordData.definitions.length ? ` (${wordData.definitions.length})` : ''}:</span>
                      {lockedFields.includes('definitions') && (
                        <Lock size={12} className="text-amber-500" title="Field locked - protected from AI updates" />
                      )}
                    </div>
                  } 
                  defaultOpen 
                  onRefresh={locked ? null : () => onRefreshSection('definitions')}
                  headerExtra={
                    <div className="flex items-center gap-1">
                      {onToggleFieldLock && (
                        <button
                          className="p-1 rounded hover:bg-muted/50 transition-colors"
                          onClick={(e) => { e.stopPropagation(); onToggleFieldLock('definitions'); }}
                          title={lockedFields.includes('definitions') ? 'Unlock field' : 'Lock field'}
                          data-testid="toggle-lock-definitions"
                        >
                          {lockedFields.includes('definitions') ? <Lock size={14} className="text-amber-500" /> : <Unlock size={14} className="text-muted-foreground" />}
                        </button>
                      )}
                      {!editMode && (
                        <button
                          className="p-1 rounded hover:bg-muted/50 transition-colors"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setEditMode(true); 
                            setEditedDefinition(Array.isArray(wordData.definitions) ? wordData.definitions.map(d => d.definition).join('\n') : '');
                          }}
                          title={isAdmin ? "Edit definitions" : "Suggest correction"}
                          data-testid="edit-definitions-btn"
                        >
                          <Pencil size={14} className="text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                  }
                >
                  {editMode ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedDefinition}
                        onChange={(e) => setEditedDefinition(e.target.value)}
                        placeholder="Enter definitions (one per line)"
                        rows={4}
                        className="text-sm"
                        data-testid="edit-definitions-textarea"
                      />
                      {!isAdmin && (
                        <Input
                          value={suggestionReason}
                          onChange={(e) => setSuggestionReason(e.target.value)}
                          placeholder="Reason for correction (optional)"
                          className="text-sm"
                          data-testid="suggestion-reason-input"
                        />
                      )}
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => { setEditMode(false); setEditedDefinition(''); setSuggestionReason(''); }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const newDefs = editedDefinition.split('\n').filter(Boolean).map(d => ({ definition: d.trim(), pos: '' }));
                            if (isAdmin && onSaveEdit) {
                              onSaveEdit('definitions', newDefs);
                            } else if (onSubmitSuggestion) {
                              onSubmitSuggestion('definitions', newDefs, suggestionReason);
                            }
                            setEditMode(false);
                            setEditedDefinition('');
                            setSuggestionReason('');
                          }}
                          disabled={actionLoading || !editedDefinition.trim()}
                          className="flex-1"
                          data-testid="save-definitions-btn"
                        >
                          {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          <span className="ml-1">{isAdmin ? 'Save' : 'Suggest'}</span>
                        </Button>
                      </div>
                      {!isAdmin && (
                        <p className="text-xs text-muted-foreground text-center">
                          Your suggestion will be saved for your view and submitted for admin review
                        </p>
                      )}
                    </div>
                  ) : (
                    wordData.definitions?.length > 0 && Array.isArray(wordData.definitions) ? (
                      wordData.definitions.map((def, idx) => (
                        <div
                          key={`def-${idx}`}
                          className="forge-field-box"
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 6,
                            cursor: 'pointer',
                            borderLeft: def.preferred ? '3px solid #D4AF37' : '3px solid transparent',
                            paddingLeft: 6,
                          }}
                          onClick={() => onTogglePreferred(idx)}
                          title={def.preferred ? 'Click to unpin' : 'Click to pin as preferred'}
                          data-testid={`definition-row-${idx}`}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="forge-entry-line">
                              <span className="forge-pos">{def.pos}</span>
                              <span className="forge-entry-text">{def.definition}</span>
                              {def.preferred && <span style={{ color: '#D4AF37', fontSize: 11, marginLeft: 4 }}>&#9733;</span>}
                            </div>
                            {def.examples?.length > 0 && (
                              <p className="forge-example-inline">&quot;{def.examples[0]}&quot;</p>
                            )}
                          </div>
                          {!locked && onDeleteDefinition && (
                            <button
                              className="forge-remove-btn"
                              title="Delete this definition"
                              onClick={(e) => { e.stopPropagation(); onDeleteDefinition(idx); }}
                              style={{ flexShrink: 0, marginTop: 2 }}
                            >
                              <Minus size={12} />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="forge-empty-hint">No definitions yet</p>
                    )
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Action buttons */}
                <div className="forge-center-row gap-2 py-2 flex-wrap justify-center">
                  <button 
                    className="forge-suggest-btn" 
                    onClick={() => setShowDetails(false)}
                    data-testid="hide-details-btn"
                  >
                    Hide details
                  </button>
                  <button 
                    className="forge-suggest-btn" 
                    onClick={() => onRefreshSection(null)} 
                    disabled={locked} 
                    style={locked ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                    data-testid="ai-enrich-btn"
                  >
                    <RefreshCw size={14} style={{ display: 'inline', marginRight: 6 }} />
                    {locked ? 'Locked' : 'AI Enrich All'}
                  </button>
                </div>

                {/* Admin unlock */}
                {isAdmin && locked && onUnlockWord && (
                  <div className="forge-center-row py-1">
                    <button 
                      className="forge-suggest-btn"
                      onClick={onUnlockWord}
                      style={{ backgroundColor: '#dc2626', color: 'white', fontSize: '12px', padding: '4px 12px' }}
                      data-testid="admin-unlock-btn"
                    >
                      <Lock size={13} style={{ display: 'inline', marginRight: 4 }} />
                      Unlock (Admin)
                    </button>
                  </div>
                )}

                <div className="forge-divider" />

                {/* Translation */}
                <CollapsibleSection 
                  title={
                    <div className="flex items-center gap-2">
                      <span>My translation:</span>
                      {lockedFields.includes('translation_en') && (
                        <Lock size={12} className="text-amber-500" title="Translation locked — protected from AI updates" />
                      )}
                    </div>
                  }
                  defaultOpen
                  headerExtra={
                    onToggleFieldLock && (
                      <button
                        className="p-1 rounded hover:bg-muted/50 transition-colors"
                        onClick={(e) => { e.stopPropagation(); onToggleFieldLock('translation_en'); }}
                        title={lockedFields.includes('translation_en') ? 'Unlock translation — AI may update it' : 'Lock translation — protect from AI updates'}
                        data-testid="toggle-lock-translation"
                      >
                        {lockedFields.includes('translation_en') ? <Lock size={14} className="text-amber-500" /> : <Unlock size={14} className="text-muted-foreground" />}
                      </button>
                    )
                  }
                >
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="add translation..."
                        value={translationText}
                        onChange={(e) => { setTranslationText(e.target.value); }}
                        className="forge-notes-area flex-1"
                        data-testid="translation-input"
                      />
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={onAutoTranslate} 
                        disabled={actionLoading}
                        title="Auto translate"
                        data-testid="auto-translate-btn"
                      >
                        <Globe className="h-4 w-4" />
                      </Button>
                    </div>
                    {translationText && !translationSaved && (
                      <Button size="sm" variant="outline" onClick={onSaveTranslation} disabled={actionLoading} className="w-full" data-testid="save-translation-btn">
                        Save Translation
                      </Button>
                    )}
                    {translationSaved && (
                      <p className="text-xs text-green-500 text-center">✓ Saved</p>
                    )}
                  </div>
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Preferred definitions */}
                <CollapsibleSection title="Preferred definitions:">
                  {Array.isArray(wordData.definitions) && wordData.definitions.filter(d => d.preferred).length > 0 ? (
                    wordData.definitions.map((def, idx) => def.preferred ? (
                      <div key={`pref-def-${idx}`} className="forge-field-box" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="forge-entry-line">
                            <span className="forge-pos">{def.pos}</span>
                            <span className="forge-entry-text">{def.definition}</span>
                          </div>
                        </div>
                        <button
                          className="forge-remove-btn"
                          title="Remove from preferred"
                          onClick={() => onTogglePreferred(idx)}
                          style={{ flexShrink: 0, marginTop: 2 }}
                          data-testid={`remove-preferred-${idx}`}
                        >
                          <Minus size={12} />
                        </button>
                      </div>
                    ) : null)
                  ) : (
                    <p className="forge-empty-hint">Click a definition above to pin it here</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Notes - multi-entry */}
                <CollapsibleSection title="Notes:">
                  <div className="space-y-2">
                    {noteEntries.map(entry => (
                      <div key={entry.id} className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-2.5 text-xs space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Note</span>
                          <button
                            onClick={() => handleDeleteNote(entry.id)}
                            className="text-muted-foreground hover:text-red-400 transition-colors"
                            data-testid="delete-note-btn"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        {entry.sentence && (
                          <p className="text-muted-foreground italic text-[11px] leading-snug">"{entry.sentence}"</p>
                        )}
                        {entry.context_definition && (
                          <p className="leading-relaxed">{entry.context_definition}</p>
                        )}
                        {entry.grammatical_role && (
                          <div className="flex gap-1.5 items-start">
                            <span className="text-amber-400/70 shrink-0">Role:</span>
                            <span className="text-muted-foreground">{entry.grammatical_role}</span>
                          </div>
                        )}
                        {entry.inflection_notes && (
                          <div className="flex gap-1.5 items-start">
                            <span className="text-amber-400/70 shrink-0">Why this form:</span>
                            <span className="text-muted-foreground">{entry.inflection_notes}</span>
                          </div>
                        )}
                        {entry.literal_vs_contextual && (
                          <div className="flex gap-1.5 items-start">
                            <span className="text-amber-400/70 shrink-0">Note:</span>
                            <span className="text-muted-foreground italic">{entry.literal_vs_contextual}</span>
                          </div>
                        )}
                        {entry.text && (
                          <p className="leading-relaxed">{entry.text}</p>
                        )}
                      </div>
                    ))}
                    <Textarea
                      placeholder="add a note..."
                      value={manualNoteText}
                      onChange={(e) => setManualNoteText(e.target.value)}
                      rows={2}
                      className="forge-notes-area"
                      data-testid="notes-input"
                    />
                    {manualNoteText && (
                      <Button size="sm" variant="outline" onClick={handleAddManualNote} disabled={actionLoading} className="w-full" data-testid="add-note-btn">
                        Add Note
                      </Button>
                    )}
                  </div>
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* ── POS-aware Grammar Analysis ── */}
                {(() => {
                  const inf = wordData.inflection || {};
                  const morph = wordData.morphology || {};
                  const posRaw = (wordData.pos || '').toLowerCase();
                  const isVerb = /verb|vb\b|^v\./.test(posRaw);
                  const isNounAdj = /noun|^n\.|adj/.test(posRaw);
                  const hasInflection = Object.values(inf).some(v => v && v !== 'null' && v !== 'None');
                  const hasMorphStructure = !!(morph.word_type || morph.prefix || morph.suffix || morph.root ||
                    morph.compound_components?.length);
                  const hasLegacy = !!(wordData.root || wordData.stem || wordData.form ||
                    wordData.person || wordData.tense || wordData.mood || wordData.voice ||
                    wordData.gender || wordData.number || wordData.grammatical_case);
                  if (!hasInflection && !hasMorphStructure && !hasLegacy) return null;

                  const InfRow = ({ label, value }) => {
                    if (!value || value === 'null' || value === 'None') return null;
                    return (
                      <div className="flex items-center gap-2">
                        <span className="opacity-60 min-w-[72px]">{label}:</span>
                        <span className="font-medium capitalize">{value}</span>
                      </div>
                    );
                  };

                  return (
                    <>
                      <CollapsibleSection title={<span className="text-purple-400">Grammar Analysis</span>} defaultOpen>
                        <div className="space-y-2 text-xs">
                          {(isVerb || inf.tense || inf.mood || inf.person || inf.aspect ||
                            wordData.tense || wordData.mood || wordData.person) && (
                            <div className="bg-blue-500/10 rounded-lg p-2.5 border border-blue-500/20">
                              <div className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1.5">Conjugation</div>
                              <div className="space-y-1">
                                <InfRow label="Tense"  value={inf.tense  || wordData.tense} />
                                <InfRow label="Mood"   value={inf.mood   || wordData.mood} />
                                <InfRow label="Person" value={inf.person || wordData.person} />
                                <InfRow label="Number" value={inf.number || wordData.number} />
                                <InfRow label="Voice"  value={inf.voice  || wordData.voice} />
                                <InfRow label="Aspect" value={inf.aspect} />
                              </div>
                            </div>
                          )}
                          {(isNounAdj || inf.gender || inf.case || inf.definiteness ||
                            wordData.gender || wordData.grammatical_case) && (
                            <div className="bg-emerald-500/10 rounded-lg p-2.5 border border-emerald-500/20">
                              <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-1.5">Declension</div>
                              <div className="space-y-1">
                                <InfRow label="Gender"       value={inf.gender || wordData.gender} />
                                <InfRow label="Number"       value={inf.number || wordData.number} />
                                <InfRow label="Case"         value={inf.case   || wordData.grammatical_case} />
                                <InfRow label="Definiteness" value={inf.definiteness} />
                                <InfRow label="Degree"       value={inf.degree} />
                              </div>
                            </div>
                          )}
                          {hasMorphStructure && (
                            <div className="bg-purple-500/10 rounded-lg p-2.5 border border-purple-500/20">
                              <div className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider mb-1.5">Word Formation</div>
                              <div className="space-y-1">
                                {(wordData.root || morph.root) && (
                                  <div className="flex items-center gap-2">
                                    <span className="opacity-60 min-w-[72px]">Root:</span>
                                    <span className="font-semibold">{wordData.root || morph.root}</span>
                                    {morph.root_meaning && <span className="text-muted-foreground">— {morph.root_meaning}</span>}
                                  </div>
                                )}
                                <InfRow label="Stem" value={wordData.stem || morph.stem} />
                                {morph.prefix && (
                                  <div className="flex items-center gap-2">
                                    <span className="opacity-60 min-w-[72px]">Prefix:</span>
                                    <span className="font-medium">{morph.prefix}</span>
                                    {morph.prefix_meaning && <span className="text-muted-foreground">— {morph.prefix_meaning}</span>}
                                  </div>
                                )}
                                {morph.suffix && (
                                  <div className="flex items-center gap-2">
                                    <span className="opacity-60 min-w-[72px]">Suffix:</span>
                                    <span className="font-medium">{morph.suffix}</span>
                                    {morph.suffix_meaning && <span className="text-muted-foreground">— {morph.suffix_meaning}</span>}
                                  </div>
                                )}
                                <InfRow label="Type" value={morph.word_type} />
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleSection>
                      <div className="forge-divider" />
                    </>
                  );
                })()}

                {/* Derivations */}
                <CollapsibleSection title={`Derivations${Array.isArray(wordData.derivations) && wordData.derivations.length ? ` (${wordData.derivations.length})` : ''}:`} onRefresh={locked ? null : () => onRefreshSection('derivations')}>
                  {Array.isArray(wordData.derivations) && wordData.derivations.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.derivations.slice(0, 10).map((d, idx) => (
                        <a key={`deriv-${idx}`} href="#" onClick={(e) => { e.preventDefault(); onWordLookup?.(d); }} className="forge-cloud-link" data-testid={`derivation-${idx}`}>{d}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No derivations yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Examples */}
                <CollapsibleSection title={`Example sentences${Array.isArray(wordData.examples) && wordData.examples.length ? ` (${wordData.examples.length})` : ''}:`} onRefresh={locked ? null : () => onRefreshSection('examples')}>
                  {Array.isArray(wordData.examples) && wordData.examples.length > 0 ? (
                    <div className="forge-examples">
                      {wordData.examples.slice(0, 4).map((ex, idx) => (
                        <p key={`ex-${idx}`} className="forge-example-line">&quot;{ex}&quot;</p>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No examples yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Synonyms */}
                <CollapsibleSection title={`Synonyms${Array.isArray(wordData.synonyms) && wordData.synonyms.length ? ` (${wordData.synonyms.length})` : ''}:`} onRefresh={locked ? null : () => onRefreshSection('synonyms')}>
                  {Array.isArray(wordData.synonyms) && wordData.synonyms.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.synonyms.slice(0, 10).map((s, idx) => (
                        <a key={`syn-${idx}`} href="#" onClick={(e) => { e.preventDefault(); onWordLookup?.(s); }} className="forge-cloud-link" data-testid={`synonym-${idx}`}>{s}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No synonyms yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Collocates */}
                <CollapsibleSection title={`Collocates${Array.isArray(wordData.collocates) && wordData.collocates.length ? ` (${wordData.collocates.length})` : ''}:`} onRefresh={locked ? null : () => onRefreshSection('collocates')}>
                  {Array.isArray(wordData.collocates) && wordData.collocates.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.collocates.slice(0, 10).map((c, idx) => (
                        <a key={`coll-${idx}`} href="#" onClick={(e) => { e.preventDefault(); onWordLookup?.(c); }} className="forge-cloud-link" data-testid={`collocate-${idx}`}>{c}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No collocates yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Categories */}
                <CollapsibleSection title={`Categories${Array.isArray(wordData.categories) && wordData.categories.length ? ` (${wordData.categories.length})` : ''}:`}>
                  {Array.isArray(wordData.categories) && wordData.categories.length > 0 ? (
                    <div className="space-y-1">
                      {wordData.categories.map((id, idx) => (
                        <div key={idx} className="forge-entry-line">
                          <span className="forge-pos">{id}</span>
                          <span className="forge-entry-text">{WORD_CATEGORIES[id] || `Category ${id}`}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No categories yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Related expressions */}
                <CollapsibleSection title={`Related words and expressions${Array.isArray(wordData.related_expressions) && wordData.related_expressions.length ? ` (${wordData.related_expressions.length})` : ''}:`} onRefresh={locked ? null : () => onRefreshSection('related_expressions')}>
                  {Array.isArray(wordData.related_expressions) && wordData.related_expressions.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.related_expressions.slice(0, 8).map((expr, idx) => (
                        <a key={`rel-${idx}`} href="#" onClick={(e) => { e.preventDefault(); onWordLookup?.(expr); }} className="forge-cloud-link" data-testid={`related-expr-${idx}`}>{expr}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No related expressions yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Searches */}
                <CollapsibleSection title="Searches:">
                  <div className="forge-search-buttons">
                    {searchLinks.map((link) => (
                      <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" className="forge-mini-search-btn" data-testid={`search-${link.label}`}>
                        {link.label}
                      </a>
                    ))}
                  </div>
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Word SRS stats */}
                <CollapsibleSection title="Word data:">
                  <div className="font-mono text-xs opacity-60 whitespace-pre-wrap" data-testid="word-stats">
                    {(() => {
                      const us = wordData.user_state || {};
                      const strength = us.strength || 0;
                      const stability = us.stability || 1.0;
                      const reviews = us.review_count || 0;
                      const lastSeen = us.last_seen_at;
                      const nextDue = us.next_review_due;
                      let sinceStr = '—';
                      if (lastSeen) {
                        const diff = (Date.now() - new Date(lastSeen).getTime()) / 3600000;
                        sinceStr = diff < 1 ? `${Math.round(diff * 60)}m ago`
                          : diff < 24 ? `${diff.toFixed(1)}h ago`
                          : `${(diff / 24).toFixed(1)}d ago`;
                      }
                      let dueStr = '—';
                      if (nextDue) {
                        const diff = (new Date(nextDue).getTime() - Date.now()) / 3600000;
                        dueStr = diff <= 0 ? 'now'
                          : diff < 1 ? `in ${Math.round(diff * 60)}m`
                          : diff < 24 ? `in ${diff.toFixed(1)}h`
                          : `in ${(diff / 24).toFixed(1)}d`;
                      }
                      // Compute decayed strength preview
                      const decayRate = 0.001;
                      const hoursElapsed = lastSeen ? (Date.now() - new Date(lastSeen).getTime()) / 3600000 : 0;
                      const decayed = hoursElapsed > 0 ? strength * Math.exp(-decayRate * hoursElapsed / stability) : strength;
                      return [
                        `status: ${currentStatus}`,
                        `strength: ${(strength * 100).toFixed(1)}% → ${(decayed * 100).toFixed(1)}% (decayed)`,
                        `stability: ${stability.toFixed(2)}`,
                        `exposures: ${us.exposure_count || 0}  reviews: ${reviews}  correct: ${us.correct_count || 0}`,
                        `last seen: ${sinceStr}`,
                        `next due: ${dueStr}`,
                      ].join('\n');
                    })()}
                  </div>
                </CollapsibleSection>
              </>
            )}

            {/* Show More Details button (collapsed) - only for single word mode with wordData */}
            {!showDetails && !bottomExpanded && !isExpressionMode && !isVerbFormationInFullOracle && wordData && (
              <div className="forge-center-row gap-2 py-2">
                <button 
                  className="forge-suggest-btn" 
                  onClick={() => setShowDetails(true)}
                  data-testid="toggle-details-btn"
                >
                  More details
                </button>
              </div>
            )}

            <div className="forge-divider" />

          </div>

          {/* Status Footer - show for both word and expression mode, but disable in expression mode */}
          <footer className="forge-footer flex-shrink-0">
            {isVerbFormationInFullOracle ? (
              <div className="text-center py-2 space-y-1">
                <p className="text-sm text-green-400 font-medium">{word}</p>
                {wordData?.ai_enriched && (
                  <p className="text-xs text-muted-foreground/60">AI enriched</p>
                )}
              </div>
            ) : isExpressionMode ? (
              <div className="text-center py-3 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Expression: <span className="font-medium text-foreground">{word}</span>
                </p>
                {expressionTranslation && (
                  <p className="text-sm text-primary/80 italic px-3">"{expressionTranslation}"</p>
                )}
                {!expressionTranslation && (
                  <p className="text-xs text-muted-foreground/50 italic">Translating…</p>
                )}
              </div>
            ) : (
              <>
                <div className="forge-status-row" data-testid="status-rail" style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className={`forge-status-btn small-round status-x ${currentStatus === 'ignored' ? 'ring-2 ring-white' : ''}`} title="Ignore (X)" onClick={() => onSetStatus('ignored')} disabled={actionLoading} data-testid="status-btn-ignored">
                    <span>&times;</span>
                    <span className="shortcut-hint">X</span>
                  </button>
                  {/* NEW button hidden for now */}
                  <button className={`forge-status-btn status-review ${currentStatus === 'learning' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('learning')} disabled={actionLoading} data-testid="status-btn-learning">
                    <span>Review</span>
                    <span className="shortcut-hint">R</span>
                  </button>
                  <button className={`forge-status-btn status-familiar ${currentStatus === 'familiar' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('familiar')} disabled={actionLoading} data-testid="status-btn-familiar">
                    <span>Familiar</span>
                    <span className="shortcut-hint">F</span>
                  </button>
                  <button className={`forge-status-btn status-known ${currentStatus === 'known' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('known')} disabled={actionLoading} data-testid="status-btn-known">
                    <Sparkles size={12} />
                    <span>Known</span>
                    <span className="shortcut-hint">K</span>
                  </button>
                </div>
                {/* Mark as expression — enter expression mode from single-word popover */}
                {!isExpressionMode && onExpandRight && (
                  <div className="flex items-center justify-center mt-2" data-testid="expression-rail">
                    <button
                      className="text-xs text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1 px-2 py-1 rounded border border-purple-400/20 hover:border-purple-400/50 disabled:opacity-50"
                      onClick={() => onExpandRight?.()}
                      disabled={actionLoading}
                      title="Select surrounding words to save as an expression"
                      data-testid="mark-expression-btn"
                    >
                      <Layers size={12} />
                      <span>Save as expression...</span>
                    </button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground text-center mt-2 opacity-60">Press X, R, F, or K to set status</p>
              </>
            )}
          </footer>

          {/* Bottom drag-to-resize handle — grab and pull to any height; resets on new word */}
          {!isExpressionMode && !isVerbFormationInFullOracle && wordData && (
            <div
              className="forge-bottom-tab"
              onMouseDown={onResizePointerDown}
              onTouchStart={onResizePointerDown}
              title="Drag to resize"
              data-testid="oracle-bottom-expand-tab"
              style={{ cursor: 'ns-resize', touchAction: 'none' }}
            >
              <div style={{ width: 28, height: 3, borderRadius: 2, background: 'var(--muted-foreground, #666)', opacity: 0.5 }} />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default WordPopoverPanel;
